import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Briefcase, CalendarDays, Users, AlertTriangle, Crown, ArrowUpRight, X, CheckCircle2, Clock } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import BurnRateChart from "@/components/BurnRateChart";
import DraggableDashboard from "@/components/DraggableDashboard";
import PendingActionsBlock from "@/components/PendingActionsBlock";
import FreelancesAtRiskBlock from "@/components/FreelancesAtRiskBlock";
import AdminDashboard from "@/components/AdminDashboard";
import ExpenseStatsBlock from "@/components/ExpenseStatsBlock";
import EmptyWeekCRAStatusBlock from "@/pages/dashboard/EmptyWeekCRAStatusBlock";
import InvoicePaymentStatusBlock from "@/pages/dashboard/InvoicePaymentStatusBlock";
import AutoInvoiceForecastWidget from "@/components/AutoInvoiceForecastWidget";
import UpcomingReceivablesWidget from "@/components/UpcomingReceivablesWidget";
import TestDataLeakBanner from "@/components/TestDataLeakBanner";

const PIE_COLORS = ["#2563EB", "#16A34A", "#CA8A04", "#DC2626", "#7C3AED", "#0891B2", "#DB2777", "#EA580C", "#65A30D", "#0F766E", "#9333EA", "#E11D48"];

const Stat = ({ label, value, sub, testid, to, onClick, accent }) => {
  const clickable = to || onClick;
  const isOrange = accent === "orange";
  const baseClass = `rounded-lg p-6 block border ${isOrange ? "bg-brand-orange-100/60 border-brand-orange/30" : "bg-white border-zinc-200"}`;
  const hover = clickable ? (isOrange ? "hover:border-brand-orange hover:shadow-md transition-all cursor-pointer" : "hover:border-brand-navy hover:shadow-md transition-all cursor-pointer") : "";
  const valueColor = isOrange ? "text-brand-orange-600" : "text-brand-ink";
  const content = (
    <div className={`${baseClass} ${hover}`} data-testid={testid}>
      <div className={`text-[11px] uppercase tracking-[0.2em] mb-3 ${isOrange ? "text-brand-orange-600" : "text-zinc-500"}`}>{label}</div>
      <div className={`font-heading text-4xl font-bold tracking-tight ${valueColor}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-2 font-mono">{sub}</div>}
      {clickable && <div className={`text-[10px] mt-2 font-mono flex items-center gap-1 ${isOrange ? "text-brand-orange-600" : "text-zinc-400"}`}>Ouvrir <ArrowUpRight size={10} /></div>}
    </div>
  );
  if (to) return <Link to={to}>{content}</Link>;
  if (onClick) return <button onClick={onClick} className="text-left w-full">{content}</button>;
  return content;
};

export default function Dashboard() {
  const { user, hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [lateList, setLateList] = useState([]);
  const [drill, setDrill] = useState(null); // 'budget' | 'consumed' | null
  const [drillData, setDrillData] = useState(null);

  // Iter-228x-bis — dashboard widgets are gated by the same permissions that
  // gate the sidebar (AppLayout). When the CEO revokes a permission for a
  // user, the corresponding dashboard block disappears too, keeping the UI
  // coherent with the user's effective access.
  const has = (p) => hasPermission(p);
  const canViewMissions = has("manage_missions") || has("view_all_missions") || has("manage_own_clients");
  const canValidateCra = has("validate_cra");
  const canInvoicing = has("invoicing");
  const canViewArAging = has("view_ar_aging");

  useEffect(() => {
    if (!drill) { setDrillData(null); return; }
    api.get(`/dashboard/breakdown?kind=${drill}`).then((r) => setDrillData(r.data)).catch(() => setDrillData(null));
  }, [drill]);

  useEffect(() => {
    (async () => {
      try {
        if (user.role === "COLLABORATOR") {
          const c = await api.get("/collaborator/dashboard");
          setData({ collaborator: c.data });
        } else if (user.role === "MANAGER") {
          const [dash, port, late] = await Promise.all([
            api.get("/manager/dashboard"),
            api.get("/manager/portfolio"),
            api.get("/manager/late-submissions"),
          ]);
          setData({ missions: dash.data, portfolio: port.data });
          setLateList(late.data);
        } else if (user.role === "GESTIONNAIRE") {
          const g = await api.get("/gestionnaire/dashboard");
          setData({ gestionnaire: g.data });
        } else if (user.role === "ADMIN") {
          // Iter-41: ADMIN dashboard is computed inside AdminDashboard component (no
          // delivery/billing/JH indicators are loaded here).
          setData({ admin: true });
        } else {
          const o = await api.get("/ceo/overview");
          setData(o.data);
          try {
            const late = await api.get("/manager/late-submissions");
            setLateList(late.data);
          } catch {}
        }
      } catch {}
    })();
  }, [user.role]);

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-[1400px]">
      <div className="flex flex-wrap gap-3 items-baseline justify-between mb-10">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-3 font-mono">
            {user.role} · {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight" data-testid="dashboard-heading">
            Welcome back, {user.name.split(" ")[0]}.
          </h1>
        </div>
      </div>

      {/* Iter-228x-quater — TEST_ leak alert (CEO/ADMIN only). Renders nothing
          when there are no leaks, so it stays invisible in healthy production
          environments. */}
      <TestDataLeakBanner user={user} />

      {user.role === "COLLABORATOR" && data?.collaborator && (
        <CollaboratorDashboard c={data.collaborator} />
      )}

      {/* Iter-193u — Dashboard expense stats widget. Visible to all roles
          (everyone has personal NDF). Toggle to global view appears for
          CEO/GESTIONNAIRE only. ADMIN doesn't see it (ADMIN has its own
          dedicated dashboard component). */}
      {user.role !== "ADMIN" && (
        <div className="mb-8">
          <ExpenseStatsBlock user={user} />
        </div>
      )}

      {(user.role === "MANAGER") && data && (
        <DraggableDashboard
          role="MANAGER"
          sections={[
            { id: "pending-actions", label: "Pending actions", render: () => <PendingActionsBlock /> },
            ...(canViewMissions ? [
            {
              id: "manager-kpis",
              label: "Key indicators",
              render: () => (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <Stat label="Active missions" value={data.portfolio?.counts?.active ?? 0} testid="stat-missions-active" to="/missions" />
                  <Stat
                    label="Alertes rouges"
                    value={data.missions.filter((m) => m.stats.health === "red").length}
                    testid="stat-red"
                    accent="orange"
                  />
                  <Stat
                    label="Consumed (MD)"
                    value={data.portfolio?.consumed_jh_total?.toFixed?.(1) ?? data.missions.reduce((a, m) => a + m.stats.consumed_jh, 0).toFixed(1)}
                    sub={`${(((data.portfolio?.consumed_jh_total || 0) / ((data.portfolio?.budget_jh_total || 1))) * 100).toFixed(0)}% du budget`}
                    testid="stat-consumed"
                    onClick={() => setDrill("budget")}
                  />
                  <Stat label="CRA en retard" value={lateList.length} testid="stat-late" accent="orange" />
                </div>
              ),
            },
            {
              id: "manager-portfolio",
              label: "Mon portefeuille",
              render: () => data.portfolio ? (
                <div>
                  <div className="flex flex-wrap gap-3 items-baseline justify-between mb-4">
                    <h2 className="font-heading text-xl font-semibold tracking-tight">Mon portefeuille</h2>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">Stats contractuelles</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                    <Stat label="Clients actifs" value={data.portfolio.counts.active_clients} testid="stat-active-clients" to="/clients" accent="orange" />
                    <Stat label="Budget total (JH)" value={data.portfolio.budget_jh_total} testid="stat-port-budget" onClick={() => setDrill("budget")} />
                    <Stat label="En attente CEO" value={data.portfolio.counts.pending_validation} testid="stat-pending" to="/missions" />
                    <Stat label="Brouillons" value={data.portfolio.counts.draft} testid="stat-drafts" to="/missions" />
                    <Stat label="Closed missions" value={data.portfolio.counts.closed} testid="stat-closed" to="/missions?status=CLOSED" accent="orange" />
                  </div>
                </div>
              ) : null,
            },
            {
              id: "manager-missions-active",
              label: "My active missions",
              render: () => (
                <div>
                  <div className="flex flex-wrap gap-3 items-baseline justify-between mb-4">
                    <h2 className="font-heading text-xl font-semibold tracking-tight">My active missions</h2>
                    <Link to="/missions/new" data-testid="create-mission-cta" className="text-sm text-brand-navy underline underline-offset-4 decoration-brand-navy/30 hover:decoration-brand-navy">+ New mission</Link>
                  </div>
                  <MissionCards missions={data.missions.filter(m => m.status === "ACTIVE")} />
                </div>
              ),
            },
            ] : []),
            ...(canValidateCra ? [
            {
              id: "manager-late-cra",
              label: "CRA en retard",
              render: () => lateList.length > 0 ? (
                <div>
                  <h2 className="font-heading text-xl font-semibold tracking-tight mb-4 flex items-center gap-2">
                    <AlertTriangle size={18} className="text-brand-orange" />
                    CRA en retard
                  </h2>
                  <div className="bg-white border border-zinc-200 rounded-lg divide-y divide-zinc-100">
                    {lateList.map((l) => (
                      <div key={l.user_id} className="flex items-center justify-between px-5 py-4" data-testid={`late-row-${l.user_id}`}>
                        <div>
                          <div className="font-medium">{l.name}</div>
                          <div className="text-xs text-zinc-500 font-mono">{l.email} · CRA {String(l.year)}-{String(l.month).padStart(2, "0")}</div>
                        </div>
                        <button
                          data-testid={`remind-btn-${l.user_id}`}
                          onClick={async () => {
                            const { toast } = await import("sonner");
                            await api.post(`/manager/remind/${l.user_id}`);
                            toast.success(`Reminder sent to ${l.name}`);
                          }}
                          className="px-4 h-9 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800"
                        >
                          Remind
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null,
            },
            ] : []),
          ]}
        />
      )}

      {user.role === "GESTIONNAIRE" && data?.gestionnaire && (
        <GestionnaireDashboard g={data.gestionnaire} />
      )}

      {user.role === "ADMIN" && data?.admin && <AdminDashboard />}

      {(user.role === "CEO") && data && data.totals && (
        <DraggableDashboard
          role={user.role}
          sections={[
            { id: "pending-actions", label: "Pending actions", render: () => <PendingActionsBlock /> },
            // Iter-194s — CEO Dashboard widget: team CRA status of the current
            // ISO week (mirrors the Friday-14h reminder cron's eligibility
            // filter). Manual "Relancer" buttons trigger the email outside
            // of the cron window.
            { id: "ceo-empty-week-cra", label: "Team CRA status", render: () => <EmptyWeekCRAStatusBlock /> },
            // Iter-194u — Invoice payment status counters (active / approaching / overdue / paid).
            { id: "ceo-invoice-payment-status", label: "Invoice payment status", render: () => <InvoicePaymentStatusBlock /> },
            // Iter-196 — Cash-flow strip: 14-day auto-invoice forecast on the
            // left, 30-day expected receivables on the right. Same widgets
            // available on the Invoices page so the CEO can pick whichever
            // surface they're on.
            {
              id: "ceo-cashflow-strip",
              label: "Cash flow — sortants & encaissements",
              render: () => (
                <div
                  className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr),320px] gap-4 items-start"
                  data-testid="dashboard-cashflow-strip"
                >
                  <div className="min-w-0">
                    <AutoInvoiceForecastWidget />
                  </div>
                  <UpcomingReceivablesWidget days={30} />
                </div>
              ),
            },
            {
              id: "ceo-kpis",
              label: "Key indicators",
              render: () => (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-6">
                  <Stat label="Clients" value={data.totals.clients} testid="stat-clients" to="/clients" />
                  <Stat label="Active missions" value={data.totals.missions_active ?? data.totals.missions} testid="stat-missions" to="/missions" />
                  <Stat label="Closed missions" value={data.totals.missions_closed ?? 0} testid="stat-closed" to="/missions?status=CLOSED" accent="orange" />
                  <Stat label="Users" value={data.totals.users} testid="stat-users" to="/admin/users" />
                  <Stat label="Budget JH" value={data.totals.budget_jh} testid="stat-budget" onClick={() => setDrill("budget")} />
                  <Stat label="Consumed" value={data.totals.consumed_jh} sub={`${((data.totals.consumed_jh/(data.totals.budget_jh||1))*100).toFixed(0)}% of budget`} testid="stat-consumed" onClick={() => setDrill("consumed")} accent="orange" />
                </div>
              ),
            },
            {
              id: "ceo-portfolio-health",
              label: "Portfolio health",
              render: () => (
                <div className="bg-white border border-zinc-200 rounded-lg p-6">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-5">Portfolio health</div>
                  <div className="flex items-center gap-1 h-3 rounded-full overflow-hidden bg-zinc-100">
                    {(() => {
                      const open = data.totals.health.green + data.totals.health.orange + data.totals.health.red;
                      return open > 0 && (
                        <>
                          <div style={{ width: `${(data.totals.health.green / open) * 100}%`, background: "#16A34A" }} />
                          <div style={{ width: `${(data.totals.health.orange / open) * 100}%`, background: "#F59E0B" }} />
                          <div style={{ width: `${(data.totals.health.red / open) * 100}%`, background: "#DC2626" }} />
                        </>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-6 mt-4 text-xs font-mono text-zinc-600">
                    <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-green-600" /> {data.totals.health.green} on track</div>
                    <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500" /> {data.totals.health.orange} watch</div>
                    <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-600" /> {data.totals.health.red} at risk</div>
                  </div>
                </div>
              ),
            },
            {
              id: "ceo-missions",
              label: "Missions",
              render: () => (
                <div>
                  <h2 className="font-heading text-xl font-semibold tracking-tight mb-4">Missions</h2>
                  <MissionCards missions={data.missions || []} />
                </div>
              ),
            },
            {
              id: "ceo-at-risk",
              label: "Missions at risk",
              render: () => <AtRiskMissions missions={data.missions || []} />,
            },
            {
              id: "ceo-freelances-at-risk",
              label: "Freelances at risk",
              render: () => <FreelancesAtRiskBlock />,
            },
          ]}
        />
      )}

      {/* Budget / Consumed drill-down pie modal */}
      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-2xl w-[95vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle>
              {drill === "budget" ? "Budget JH — breakdown by mission" : "Consumed JH — breakdown by mission"}
            </DialogTitle>
          </DialogHeader>
          {drillData && drillData.slices.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-3">
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={drillData.slices.map(s => ({ name: s.title, value: s.value }))} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={(e) => `${e.value}`}>
                    {drillData.slices.map((s, i) => <Cell key={i} fill={s.color || PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => [`${v} JH`, "Value"]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="max-h-[280px] overflow-y-auto space-y-1">
                {drillData.slices.map((s, i) => (
                  <Link key={s.mission_id} to={`/missions/${s.mission_id}`} onClick={() => setDrill(null)}
                        data-testid={`drill-row-${s.mission_id}`}
                        className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-zinc-50 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s.color || PIE_COLORS[i % PIE_COLORS.length] }} />
                      <div className="min-w-0">
                        <div className="truncate font-medium">{s.title}</div>
                        <div className="text-[10px] text-zinc-500 font-mono">{s.client_name}</div>
                      </div>
                    </div>
                    <div className="text-xs font-mono font-medium">{s.value} JH</div>
                  </Link>
                ))}
                <div className="flex items-center justify-between px-3 py-2 border-t border-zinc-200 mt-2 font-mono text-xs">
                  <div className="text-zinc-500 uppercase tracking-widest">Total</div>
                  <div className="font-semibold">{drillData.total} JH</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-zinc-500 text-center py-10">No data yet.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AtRiskMissions({ missions }) {
  // We only fetch burn-rate for the 5 most-consumed missions (optimistic perf).
  const [atRisk, setAtRisk] = useState([]);
  useEffect(() => {
    if (!missions || missions.length === 0) return;
    // Iter-284 — closed missions are archived: never flagged as at-risk.
    const candidates = [...missions]
      .filter((m) => m.status !== "CLOSED")
      .sort((a, b) => (b.stats?.consumed_jh || 0) - (a.stats?.consumed_jh || 0))
      .slice(0, 5);
    (async () => {
      const results = [];
      for (const m of candidates) {
        try {
          const r = await api.get(`/missions/${m.mission_id}/burn-rate`);
          if (r.data.projection_end_jh != null && r.data.budget_jh > 0 && r.data.projection_end_jh > r.data.budget_jh) {
            results.push({ ...m, burn: r.data });
          }
        } catch { /* ignore */ }
      }
      setAtRisk(results);
    })();
  }, [missions]);
  if (atRisk.length === 0) return null;
  return (
    <div className="mt-12">
      <h2 className="font-heading text-xl font-semibold tracking-tight mb-4 flex items-center gap-2">
        <AlertTriangle size={18} className="text-red-600" /> At-risk missions (projected overrun)
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {atRisk.map((m) => (
          <div key={m.mission_id} data-testid={`at-risk-${m.mission_id}`}>
            <div className="mb-2 flex items-center justify-between">
              <Link to={`/missions/${m.mission_id}`} className="text-sm font-semibold hover:underline">{m.title}</Link>
              <span className="text-[10px] font-mono text-zinc-500">{m.client_name}</span>
            </div>
            <BurnRateChart missionId={m.mission_id} compact />
          </div>
        ))}
      </div>
    </div>
  );
}

function CollaboratorDashboard({ c }) {
  const { hasPermission } = useAuth();
  const canTimesheet = hasPermission("timesheet");
  const k = c.kpi;
  const subStatus = k.current_month_status;
  const subLabel = {
    NOT_SUBMITTED: { label: "Non soumis", color: "text-zinc-600", bg: "bg-zinc-100", icon: Clock },
    DRAFT: { label: "Brouillon", color: "text-amber-800", bg: "bg-amber-100", icon: Clock },
    SUBMITTED: { label: "Soumis", color: "text-blue-800", bg: "bg-blue-100", icon: Clock },
    APPROVED: { label: "Validated", color: "text-green-800", bg: "bg-green-100", icon: CheckCircle2 },
    REJECTED: { label: "Rejected", color: "text-red-800", bg: "bg-red-100", icon: AlertTriangle },
    UNLOCK_APPROVED: { label: "Unlocked", color: "text-amber-800", bg: "bg-amber-100", icon: Clock },
  }[subStatus] || { label: subStatus, color: "text-zinc-600", bg: "bg-zinc-100", icon: Clock };
  const SubIcon = subLabel.icon;

  return (
    <DraggableDashboard
      role="COLLABORATOR"
      sections={[
        { id: "pending-actions", label: "Pending actions", render: () => <PendingActionsBlock /> },
        // Iter-228x — When the user has had `timesheet` permission revoked,
        // hide every CRA/mission-centric block. They keep only the trend chart
        // and notifications (read-only history).
        ...(canTimesheet ? [
        {
          id: "col-kpis",
          label: "Key indicators",
          render: () => (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <Stat label="Active missions" value={k.missions_active} sub={`${k.missions_total} au total`} testid="stat-col-missions-active" accent="orange" />
              <Stat label="MD consumed (month)" value={k.consumed_mtd_jh} sub={`${k.consumed_ytd_jh} MD this year`} testid="stat-col-mtd" to="/stats" />
              <Stat label="Notifications non lues" value={k.unread_notifications} testid="stat-col-unread" />
              <div
                className="rounded-lg p-6 border bg-white border-zinc-200"
                data-testid="stat-col-submission"
              >
                <div className="text-[11px] uppercase tracking-[0.2em] mb-3 text-zinc-500">
                  CRA {k.current_month_label}
                </div>
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md ${subLabel.bg} ${subLabel.color} font-semibold`}>
                  <SubIcon size={14} />
                  <span className="text-sm">{subLabel.label}</span>
                </div>
                <Link to="/timesheet" className="text-[10px] mt-3 font-mono flex items-center gap-1 text-zinc-400 hover:text-brand-navy">
                  Open timesheet <ArrowUpRight size={10} />
                </Link>
              </div>
            </div>
          ),
        },
        {
          id: "col-cta-timesheet",
          label: "CTA Timesheet",
          render: () => (
            <Link
              to="/timesheet"
              data-testid="cta-timesheet"
              className="block bg-brand-navy text-white rounded-lg p-6 hover:bg-brand-navy-600 transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-brand-cream/70 mb-2">Action rapide</div>
                  <div className="font-heading text-2xl font-semibold">Saisir mon timesheet</div>
                </div>
                <CalendarDays size={48} strokeWidth={1.2} className="opacity-70 group-hover:opacity-100 group-hover:scale-110 transition-all" />
              </div>
            </Link>
          ),
        },
        {
          id: "col-missions",
          label: "Mes missions",
          render: () => (
            <div>
              <div className="flex flex-wrap gap-3 items-baseline justify-between mb-4">
                <h2 className="font-heading text-xl font-semibold tracking-tight">Mes missions</h2>
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">{c.missions.length} affectation(s)</span>
              </div>
              {c.missions.length === 0 ? (
                <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center text-sm text-zinc-500">
                  Vous n'êtes affecté à no mission pour l'instant.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                  {c.missions.map((m) => {
                    const pct = Math.min(100, m.pct);
                    const over = m.remaining_jh < 0;
                    const barColor = m.pct >= 100 ? "#DC2626" : m.pct >= 80 ? "#F59E0B" : m.color;
                    return (
                      // Iter-210 — collab/freelance get a READ-ONLY summary
                      // card (no link to mission detail). The TJM/HT redaction
                      // happens server-side (Iter-208), but to honour the CEO
                      // policy of "no mission detail access for these roles"
                      // we strip the navigation as well.
                      <div
                        key={m.mission_id}
                        data-testid={`col-mission-card-${m.mission_id}`}
                        className="bg-white border border-zinc-200 rounded-lg p-5"
                      >
                        <div className="flex items-start gap-3 mb-4">
                          <div className="w-1 self-stretch rounded-full" style={{ background: m.color }} />
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-mono">{m.client_name || "—"} · {m.status}</div>
                            <div className="font-heading font-semibold text-base tracking-tight truncate">{m.title}</div>
                          </div>
                          {m.pct >= 80 && (
                            <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-full font-mono">
                              <AlertTriangle size={10} className="mr-1" />{m.pct >= 100 ? "100%" : "80%"}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2 mb-4 sm:grid-cols-3">
                          <div><div className="text-[10px] text-zinc-500 uppercase tracking-wider">Allocated</div><div className="font-mono text-sm">{m.assigned_jh} JH</div></div>
                          <div><div className="text-[10px] text-zinc-500 uppercase tracking-wider">Consumed</div><div className="font-mono text-sm">{m.consumed_jh} JH</div></div>
                          <div><div className="text-[10px] text-zinc-500 uppercase tracking-wider">Reste</div><div className={`font-mono text-sm ${over ? "text-red-700 font-bold" : ""}`}>{m.remaining_jh} JH</div></div>
                        </div>
                        <div className="h-1 bg-zinc-100 rounded-full overflow-hidden">
                          <div className="h-full" style={{ width: `${pct}%`, background: barColor }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ),
        },
        {
          id: "col-trend",
          label: "Tendance 6 mois",
          render: () => (
            <div className="bg-white border border-zinc-200 rounded-lg p-6">
              <div className="flex flex-wrap gap-3 items-baseline justify-between mb-4">
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono">6-month trend — hours logged</div>
                <Link to="/stats" className="text-xs text-brand-navy hover:underline">View details</Link>
              </div>
              <div data-testid="col-trend-chart" style={{ width: "100%" }}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={c.monthly_trend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#71717A" />
                    <YAxis tick={{ fontSize: 11, fontFamily: "monospace" }} stroke="#71717A" />
                    <Tooltip formatter={(v) => [`${v} h`, "Heures"]} contentStyle={{ fontSize: 12 }} />
                    <Bar dataKey="hours" fill="#1E3A5F" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ),
        },
        ] : []),
      ]}
    />
  );
}

function GestionnaireDashboard({ g }) {
  const { hasPermission } = useAuth();
  const has = (p) => hasPermission(p);
  // Iter-228x-bis — Gestionnaire widgets gated by their underlying perms.
  const canViewMissions = has("manage_missions") || has("view_all_missions") || has("manage_own_clients");
  const canInvoicing = has("invoicing");
  const canViewArAging = has("view_ar_aging");
  const k = g.kpi;
  const ar = g.ar_aging;
  return (
    <DraggableDashboard
      role="GESTIONNAIRE"
      sections={[
        { id: "pending-actions", label: "Pending actions", render: () => <PendingActionsBlock /> },
        ...((canInvoicing || canViewArAging) ? [
        {
          id: "gest-kpi-top",
          label: "Revenue & AR",
          render: () => (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <Stat label="Revenue MTD" value={`${Number(k.revenue_mtd).toLocaleString()} `} sub="Pre-tax amount billed this month" testid="stat-revenue-mtd" accent="orange" />
              <Stat label="Revenue YTD" value={`${Number(k.revenue_ytd).toLocaleString()}`} testid="stat-revenue-ytd" />
              <Stat label="AR ouvert total" value={`${Number(g.ar_total_open).toLocaleString()}`} sub="Unpaid invoices" testid="stat-ar-total" accent="orange" to="/billing/queue" />
              <Stat label="Clients" value={k.clients} testid="stat-g-clients" to="/clients" />
            </div>
          ),
        },
        ] : []),
        ...(canViewMissions ? [
        {
          id: "gest-kpi-missions",
          label: "Missions & PO",
          render: () => (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <Stat label="Active missions" value={k.active_missions} testid="stat-g-active" to="/missions" />
              <Stat label="En attente validation" value={k.pending_validation} testid="stat-g-pending" to="/missions" />
              <Stat label="Closed missions" value={k.closed_missions} testid="stat-g-closed" to="/missions?status=CLOSED" />
              <Stat label="Out-of-sync POs" value={k.po_mismatch_count} sub={`sur ${k.po_count} PO`} testid="stat-g-po-mismatch" accent="orange" />
            </div>
          ),
        },
        ] : []),
        ...((canViewArAging || canInvoicing) ? [
        {
          id: "gest-ar-top",
          label: "AR Aging & Top clients",
          render: () => (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="ar-aging-card">
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-5">AR Aging</div>
                <div className="space-y-2">
                  {[
                    ["0-30 j", ar["0_30"], "#16A34A"],
                    ["31-60 j", ar["31_60"], "#F59E0B"],
                    ["61-90 j", ar["61_90"], "#D97A2A"],
                    [">90 j", ar["over_90"], "#DC2626"],
                  ].map(([label, value, color]) => (
                    <div key={label} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: color }} />
                        <span className="text-xs text-zinc-600">{label}</span>
                      </div>
                      <span className="font-mono text-sm">{Number(value).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="top-clients-card">
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-5">Top 5 clients (YTD)</div>
                {g.top_clients.length === 0 ? (
                  <div className="text-xs text-zinc-500 italic">No invoice this year.</div>
                ) : (
                  <div className="space-y-2">
                    {g.top_clients.map((c, i) => (
                      <div key={c.client_id} className="flex items-center justify-between py-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-5 h-5 rounded-full bg-brand-cream flex items-center justify-center text-[10px] font-bold text-brand-navy">{i+1}</span>
                          <span className="text-sm truncate">{c.name}</span>
                        </div>
                        <span className="font-mono text-sm">{Number(c.revenue_ytd).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ),
        },
        ] : []),
      ]}
    />
  );
}

function MissionCards({ missions }) {
  if (!missions || missions.length === 0) {
    return <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center text-sm text-zinc-500">No missions yet.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      {missions.map((m) => {
        const s = m.stats || {};
        const healthColor = { green: "#16A34A", orange: "#F59E0B", red: "#DC2626" }[s.health] || "#71717A";
        const pct = s.budget_jh ? Math.min(100, (s.consumed_jh / s.budget_jh) * 100) : 0;
        return (
          <Link
            key={m.mission_id}
            to={`/missions/${m.mission_id}`}
            className="group bg-white border border-zinc-200 rounded-lg p-5 hover:shadow-md hover:border-zinc-300 transition-all"
            data-testid={`mission-card-${m.mission_id}`}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-1 self-stretch rounded-full" style={{ background: m.color }} />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1 font-mono">{m.client_name || "—"} · {m.type}</div>
                <div className="font-heading font-semibold text-base tracking-tight truncate">{m.title}</div>
              </div>
              <span className="w-2 h-2 rounded-full" style={{ background: healthColor }} title={s.health} />
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4 sm:grid-cols-3">
              <div><div className="text-[10px] text-zinc-500 uppercase tracking-wider">Budget</div><div className="font-mono text-sm">{s.budget_jh || 0} JH</div></div>
              <div><div className="text-[10px] text-zinc-500 uppercase tracking-wider">Consumed</div><div className="font-mono text-sm">{s.consumed_jh ?? 0} JH</div></div>
              <div><div className="text-[10px] text-zinc-500 uppercase tracking-wider">CRA</div><div className="font-mono text-sm">{s.cra_pct ?? 0}%</div></div>
            </div>
            <div className="h-1 bg-zinc-100 rounded-full overflow-hidden">
              <div className="h-full" style={{ width: `${pct}%`, background: m.color }} />
            </div>
          </Link>
        );
      })}
    </div>
  );
}
